import {
  BgColorsOutlined,
  CheckCircleOutlined,
  DownOutlined,
  LoginOutlined,
  StopOutlined,
  UpOutlined,
} from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { RouteMap } from '@components/RouteMap'
import { ConditionalWrapper } from '@components/_SiteWide/Layout/ConditionalWrapper'
import { modes } from '@constants/orderWizard'
import { GraviButton, Horizontal, Texto, Vertical, useThemeContext } from '@gravitate-js/excalibrr'
import { useModelConfig } from '@hooks/useModelConfig'
import { getCarrierDeliveryWindow, isDefined } from '@utils/general'
import { Col, Flex, Row, Switch, Tooltip } from 'antd'
import useMediaQuery from 'use-media-antd-query'

import { useLocations } from '@/api/useMetadata/useMasterMetadata'
import { useLockedOrders } from '@hooks/useLockedOrders'
import { formatTimeWindow } from '../../../_SiteWide/OrderDisplay/OrderTimeFormats'
import { darkMapTheme } from '../../../_SiteWide/Theming/darkMapTheme'
import { useWizardMaster } from '../../Contexts/WizardMasterContext'
import { DriverNotePreview } from './DisplayComponents/DriverNotePreview'
import { AddNoLoad } from './DisplayComponents/NoLoadPopover'
import { StopCard } from './DisplayComponents/StopCards'
import { SupplyOptionCard } from './DisplayComponents/SupplyOptions/SupplyOptionCard'
import type { OrderStopAndMeta, OrderStopWithMeta, StopTypes } from './types'
import { isOrderEditable, showResequenceButtons, sortOrderStops } from './util'
import { useDestinationsContext } from '../../Contexts/DestinationsContext'

export function OrderPreview() {
  const {
    orderStops,
    setIsComplete,
    driverNote,
    showAdditiveCheckbox,
    isAdditiveCheckboxEnabled,
    payload,
    setPayload,
    setWizardOrder,
    wizardOrder,
    setMode,
    mode,
  } = useWizardMaster()

  const { isDarkTheme } = useThemeContext()

  if (wizardOrder.state === 'complete') {
    setIsComplete(true)
  }

  function handleAdditiveChange(checked) {
    setWizardOrder({ ...structuredClone(wizardOrder), has_additives: checked })
    setPayload({ ...structuredClone(payload), has_additives: checked })
    if (mode !== modes.CREATE) {
      setMode(modes.EDIT)
    }
  }

  return (
    <Vertical style={{ overflow: 'auto' }}>
      {driverNote && <DriverNotePreview />}

      <SupplyOptionCard isBest selected supplyOption={wizardOrder?.supply_option} showLockIn={false} />
      <Row style={{ flex: 1, paddingBottom: '1rem' }}>
        <Col span={8}>
          <div className='order-map-container mt-2 mx-3'>
            {orderStops && <RouteMap stops={orderStops} options={isDarkTheme && darkMapTheme} />}
          </div>
        </Col>
        <Col span={16}>
          <Vertical justifyContent='space-between' className='order-details-flex pr-3 pl-2'>
            <OrderStopTimeline />
            <OrderSummary
              order={wizardOrder}
              isAdditiveCheckboxEnabled={isAdditiveCheckboxEnabled}
              showAdditiveCheckbox={showAdditiveCheckbox}
              handleAdditiveChange={handleAdditiveChange}
            />
          </Vertical>
        </Col>
      </Row>
    </Vertical>
  )
}

export function OrderStopTimeline() {
  const { scopes } = useSecurityContext()
  const { config } = useModelConfig()
  const { lockedOrders } = useLockedOrders()
  const { data: locations } = useLocations('Terminal')
  const { resequenceOrderStops, handleDeleteDetour, handleAddDetour, wizardOrder, isComplete } = useWizardMaster()

  const { drops, loads } = wizardOrder
  const isEditable = isOrderEditable(lockedOrders, wizardOrder, scopes, isComplete)
  const allowTerminalSwaps = (wizardOrder.allow_terminal_swaps ?? true) && config.allow_terminal_swaps

  const stopList = sortOrderStops(loads, wizardOrder.detours, drops)
  const allowAddDetour = scopes?.create_order && wizardOrder.state === 'in progress'
  return (
    <Vertical className='drop-container'>
      {stopList.map((stop, index) => {
        const showNewLeg = index > 0 && stopList[index - 1].driver_schedule_id !== stop.driver_schedule_id
        const isPrevComplete = index > 0 && isDefined(stopList[index - 1].completed_at)
        return (
          <Vertical key={index} flex='none' height={'auto'}>
            <Horizontal justifyContent='space-between' alignItems='flex-end' className='mb-3'>
              {showNewLeg && (
                <Texto className='driver-leg-detail'>
                  This leg will be ran by <b>{stop.driver}.</b>
                </Texto>
              )}
              {(index === 0 || showNewLeg) && allowAddDetour && (
                <AddNoLoad
                  driverName={stop.driver}
                  driverScheduleId={stop.driver_schedule_id}
                  terminalOptions={locations?.results}
                  handleAddDetour={handleAddDetour}
                />
              )}
            </Horizontal>
            <StopPreview
              key={stop.location_name}
              index={index}
              length={wizardOrder[stop.itemType + 's']?.length || 0}
              allowTerminalSwaps={allowTerminalSwaps}
              stop={stop}
              isPrevComplete={isPrevComplete}
              showEditControls={isEditable && wizardOrder[stop.itemType + 's']?.length > 1}
              resequenceOrderStops={resequenceOrderStops}
              handleDeleteDetour={handleDeleteDetour}
            />
          </Vertical>
        )
      })}
    </Vertical>
  )
}

export function OrderSummary({ order, showAdditiveCheckbox, isAdditiveCheckboxEnabled, handleAdditiveChange }) {
  const { formatVolume } = useFormatter({})
  const formattedValue = formatVolume(order.total_gallons, true)?.toUpperCase()
  const bp = useMediaQuery()
  const showDeliveryWindow = Boolean(order.id)
  const hasCarrier = order.carrier || order.manager

  return (
    <Row className='order-summary-row'>
      <Col span={16} style={{ borderRadius: '5px 0px 0px 5px' }}>
        <div className='vertical-flex-center'>
          <div className='detail-data-label'>Delivery Window</div>
          <Texto
            category='h5'
            weight='bold'
            className={`detail-data-value ${showDeliveryWindow && 'uppercase'}`}
            appearance={showDeliveryWindow ? 'default' : 'light'}
          >
            {showDeliveryWindow
              ? hasCarrier
                ? getCarrierDeliveryWindow(order)
                : formatTimeWindow(
                    order.dispatch_window_start,
                    order.dispatch_window_end,
                    false,
                    false,
                    order.dispatch_window_start_local
                  )
              : 'Available after order is created'}
          </Texto>
        </div>
      </Col>

      <Col span={8} className='summary-total-col'>
        <ConditionalWrapper
          condition={bp === 'xxl'}
          trueWrapper={(props) => (
            <Horizontal alignItems='center' style={{ height: '100%' }} justifyContent='space-between'>
              {props.children}
            </Horizontal>
          )}
          falseWrapper={(props) => (
            <Vertical verticalCenter gap='1rem'>
              {props.children}
            </Vertical>
          )}
        >
          <Horizontal verticalCenter>
            <ConditionalWrapper
              condition={bp === 'xxl'}
              falseWrapper={
                showAdditiveCheckbox
                  ? (props) => (
                      <Horizontal verticalCenter style={{ gap: '1rem', flex: 1 }}>
                        {props.children}
                      </Horizontal>
                    )
                  : null
              }
              trueWrapper={(props) => <Vertical>{props.children}</Vertical>}
            >
              <Texto style={{ flex: 1 }} appearance='white' className=''>
                Total Volume
              </Texto>
              <Texto category='h4' weight='bolder' appearance='white'>
                {formattedValue}
              </Texto>
            </ConditionalWrapper>
          </Horizontal>
          {showAdditiveCheckbox && (
            <div>
              <ConditionalWrapper
                condition={document.body.clientWidth > 1600 && document.body.clientWidth < 1950}
                trueWrapper={(props) => <Vertical className='ow-additive-switch'>{props.children}</Vertical>}
                falseWrapper={(props) => (
                  <Horizontal alignItems='center' className='ow-additive-switch' style={{ gap: '0.5rem' }}>
                    {props.children}
                  </Horizontal>
                )}
              >
                <Texto category='p2' style={{ flex: 1 }} appearance='white'>
                  Has Additives
                </Texto>
                <div>
                  <Switch
                    size={bp === 'xxl' ? 'default' : 'small'}
                    disabled={!isAdditiveCheckboxEnabled}
                    checkedChildren={<BgColorsOutlined />}
                    onChange={handleAdditiveChange}
                    defaultChecked={order.has_additives}
                  />
                </div>
              </ConditionalWrapper>
            </div>
          )}
        </ConditionalWrapper>
      </Col>
    </Row>
  )
}

function isLoad(val: OrderStopWithMeta): val is Gravitate.OrderLoad & { originalIndex: number; itemType: 'load' } {
  return val.itemType === 'load'
}
function isDrop(val: OrderStopWithMeta): val is Gravitate.OrderDrop & { originalIndex: number; itemType: 'drop' } {
  return val.itemType === 'drop'
}

const splitMeta = (stop: OrderStopWithMeta): OrderStopAndMeta =>
  isLoad(stop)
    ? { stop, type: stop.itemType }
    : isDrop(stop)
      ? { stop, type: stop.itemType }
      : { stop, type: stop.itemType }

type StopPreviewProps = {
  stop: OrderStopWithMeta
  isPrevComplete: boolean
  index: number
  length: number
  resequenceOrderStops: (fromIndex: number, toIndex: number, type: 'loads' | 'drops') => void
  handleDeleteDetour?: (detourId: string) => void
  allowTerminalSwaps?: boolean
  showEditControls: boolean | undefined
}

function StopPreview({
  stop,
  isPrevComplete,
  index,
  length,
  resequenceOrderStops,
  handleDeleteDetour,
  allowTerminalSwaps = false,
  showEditControls = true,
}: StopPreviewProps) {
  const showResequence = showResequenceButtons(
    stop.itemType,
    allowTerminalSwaps,
    showEditControls,
    stop,
    isPrevComplete,
    length
  )

  return (
    <Flex flex='1 0 auto' gap={8} className='mb-4' style={{ width: '100%', marginTop: index === 0 ? 0 : undefined }}>
      <Flex className='vertical-flex-center items-center'>
        {showResequence ? (
          stop.originalIndex === 0 || isPrevComplete ? (
            <Tooltip title={`Move ${stop.itemType} down`}>
              <GraviButton
                onClick={() =>
                  resequenceOrderStops(
                    stop.originalIndex,
                    stop.originalIndex + 1,
                    stop.itemType === 'load' ? 'loads' : 'drops'
                  )
                }
                appearance='outlined'
                theme2={stop.itemType === 'load'}
                error={stop.itemType === 'drop'}
                icon={<DownOutlined />}
              />
            </Tooltip>
          ) : (
            <Tooltip title={`Move ${stop.itemType} up`}>
              <GraviButton
                onClick={() =>
                  resequenceOrderStops(
                    stop.originalIndex,
                    stop.originalIndex - 1,
                    stop.itemType === 'load' ? 'loads' : 'drops'
                  )
                }
                theme2={stop.itemType === 'load'}
                error={stop.itemType === 'drop'}
                appearance='outlined'
                icon={<UpOutlined />}
              />
            </Tooltip>
          )
        ) : (
          <CardIcon type={stop.itemType} isComplete={isDefined(stop.completed_at)} />
        )}
      </Flex>
      <Flex flex='1 1 auto' style={{ width: '100%' }}>
        <StopCard {...splitMeta(stop)} handleDeleteDetour={handleDeleteDetour} />
      </Flex>
    </Flex>
  )
}

type CardIconProps = {
  type: StopTypes
  isDetour?: boolean
  isComplete?: boolean
}

function CardIcon({ type, isComplete }: CardIconProps) {
  if (type === 'detour') {
    return <StopOutlined className='stop-icon detour' />
  }
  if (isComplete) return <CheckCircleOutlined className={`stop-icon ${type}`} />

  return <LoginOutlined className={`stop-icon ${type}`} />
}
